---
title: Getting Started with Codex in the Entire CLI
description: Codex is now supported in the Entire CLI. Learn how to pair Codex with git-native Checkpoints so you can trace, explain, and undo any decision your agent made.
category: Engineering
author: Rizèl Scarlett
---

AI-assisted development accelerates experimentation, but working quickly comes with a cost.

Developers may have access to their code, commits, and transcripts, but the relationship between these artifacts is fragile. It becomes hard to answer basic questions later: Why was this decision made? Which prompt led to this change? What was the last known-good state before things drifted?

At Entire, we are building for that boundary. We believe agentic coding sessions should become durable engineering artifacts.

Since the [Entire CLI](https://docs.entire.io/introduction) launched, [Codex](https://docs.entire.io/integrations/codex) has been one of the most requested agents from our community. Now Codex is supported in Entire with the same durability model we use for other built-in agents: [Checkpoints](https://docs.entire.io/cli/checkpoints) tied to Git history, rewind points you can rely on, and a transparent record of how prompts became code.

## A Real Workflow Example

I put the Codex integration to the test by building a [handwriting-to-scrapbook demo](https://github.com/blackgirlbytes/pretext-handwriting-demo).

I used technologies I was new to, including [Pretext](https://github.com/chenglou/pretext), a new, open source, and [viral](https://x.com/_chenglou/status/2037713766205608234?s=20) layout engine that calculates text dimensions without the overhead of DOM measurements. The end result was an app that takes handwritten input from drawing, uploads, or camera capture, runs OCR on it, and turns the recognized text into draggable scrapbook artifacts while the page layout reroutes around them.

![Handwriting to scrapbook demo](/blog/getting-started-with-codex-in-entire-cli/handwriting-to-scrapbook-demo.png)

## Starting a Codex Session with Entire

I started by running:

```shell
entire enable --agent codex
```

This is a one-time setup per repository. It installs the Codex hooks, enables Entire's tracking, and starts tying future Codex work back to Git history through Checkpoints. Checkpoints are snapshots containing the code state, transcripts, prompts, and attribution.

### Automating Agent Discipline

To make the workflow more useful, I told Codex to commit early and often.

Frequent commits make agent-driven work much easier to track, but I do not always remember to do that myself. So I pushed that responsibility to the agent by adding explicit rules to the project's `AGENTS.md` file:

```text
Important working rules for this project:

- Keep changes and milestones separated into distinct commits.
- After each change or milestone, commit and push before starting the next one.
- Do not bundle unrelated work from different prompts into the same commit.
- If a prompt would require a major architectural deviation, stop and explain before proceeding.
```

That kept the history more structured and made the resulting Checkpoints more useful.

## Understanding What Changed Between Checkpoints

Midway through polishing the app, I explored letting users bring their own OpenAI API key so they could try the demo without consuming my credits. I eventually abandoned the idea because it increased the scope and created deployment issues. Codex, however, summarized that work with a commit message that only said:

```text
Harden API key gate interactions
```

Candidly, that message does not explain what I was trying to do, what I had already tested, or why I decided not to continue.

The command [`entire explain`](https://docs.entire.io/cli/commands#explain) fills in that missing context by letting me inspect either a commit or a checkpoint directly:

```shell
# Explain by commit
entire explain --commit <commit-sha>

# Explain by checkpoint
entire explain --checkpoint <checkpoint-id>
```

When I ran `entire explain` on the Checkpoint linked to `Harden API key gate interactions`, I got this output:

```text
Checkpoint: ed9285032052
Session: 019d473b-fa3b-7480-9725-723b0aae707f
Created: 2026-04-01 09:19:32
Author: Rizel Scarlett <rizel@entire.io>
Tokens: 43721

Commits: (1)
  200abcf 2026-04-01 Harden API key gate interactions

Intent: ok i want to set this up where i can deploy this but people have to p...
Outcome: (not generated)

Files: (2)
  - public/js/app.js
  - public/js/lib/api-key-gate.js

Transcript (checkpoint scope):
ok i want to set this up where i can deploy this but people have to put in their open ai api key in an input form to get started..so it doesn't have to be my credits..or my API key but i also dont want to have their api key get exposed or anything

---

so will this work if i deployed to vercel? will i need to add env vars

---

before the vercel thing..i entered my key but its not letting me submit. no error messages either.

---

Still not working. Need to pivot
```

That output made the checkpoint immediately legible. Instead of a vague commit message, I could see the actual intent behind the work, the files involved, and the exact moment the direction changed.

## Reverting to a Known-Good State

Because I was having issues with the bring-your-own-key implementation, I wanted to go back to the point right before it was introduced. The following command gave me a clean way to return to a known-good state:

```shell
entire rewind
```

The [`entire rewind`](https://docs.entire.io/cli/commands#rewind) command first shows a list of available Checkpoints. After selecting one, you can choose from three restore modes:

- `Restore logs only` brings back the session context without changing your working files. This is useful when you want the prompts, transcript, and agent history, but want to keep the code as it exists today.
- `Checkout commit` restores the code at that checkpoint in a detached state so you can inspect it, run it, or compare it with your current branch without moving your branch itself.
- `Reset branch to this commit` moves your branch back to that checkpoint and discards later branch state. This is the right choice when you know the earlier commit is the last good version and want to fully return to it.

In my case, I chose the checkpoint from before the bring-your-own-key implementation and reset the branch to that commit, because I knew that was the last working version and did not include any of the BYOK code.

## How Entire Integrates with Codex

Behind that workflow is the same integration pattern we use for all built-in agents, including Codex, Claude Code, Copilot, and OpenCode. Each agent gets a small adapter that tells Entire how to:

- Install agent hooks for lifecycle events like session start, prompt submit, and turn end
- Read that agent's session data
- Translate native agent events into Entire's shared lifecycle

We were able to use that same pattern for Codex, but we still had to account for a few ways it behaves differently from our other integrations.

Codex stores session history in `JSONL` files, requires hooks to be enabled through a feature flag during setup, and exposes details like file edits and token usage through its session logs. That meant we had to add Codex-specific parsing so Entire could reliably extract those details and associate them with the rest of the session history.

![Codex Adapter Animation](/blog/getting-started-with-codex-in-entire-cli/codex_adapter_integration_v5_animated_transparent.gif)

For a deeper dive into how we integrate agents, check out our [agent guide](https://github.com/entireio/cli/blob/main/docs/architecture/agent-guide.md).

## Let's Build in the Open

Codex support in Entire is in technical preview through April 30, 2026.

If you are already using Codex and want to help shape how Entire supports agentic workflows, we invite you to apply to the Entire Insiders program. Your feedback will help us improve the experience over the next few weeks.

- [Apply to the Entire Insiders program](https://forms.gle/5ToabbqD79qcwEtD6)
- [Join the Discord](https://discord.gg/jZJs3Tue4S)
- [Open an issue](https://github.com/entireio/cli/issues)
